import { useCallback, useEffect, useRef, useState } from "react";
import { LocalVideoTrack } from "livekit-client";
import { HRDMark } from "../components/HRDMark";
import { Icon } from "../components/Icons";
import { ZoomFXProcessor, FX_OFF, fxActive, type FxMode } from "../lib/fx";
import type { MeetingPublicView } from "../api/types";

export interface PreJoinChoice {
  videoDeviceId: string | null;
  audioDeviceId: string | null;
  fx: FxMode;
}

/**
 * Pre-join screen. Asks for camera/mic permissions, enumerates devices,
 * lets the user pick camera & mic, shows a live preview and an animated
 * waveform, then hands control over to InCall along with the chosen
 * deviceIds. The MediaStream is stopped right before we leave —
 * livekit-client will reacquire its own tracks on connect.
 */
export function PreJoin({
  meeting,
  onJoin,
}: {
  meeting: MeetingPublicView;
  onJoin: (choice: PreJoinChoice) => void;
}) {
  const role = meeting.role;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lkVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const fxProcRef = useRef<ZoomFXProcessor | null>(null);
  const fxModeRef = useRef<FxMode>(FX_OFF);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);

  const [streamReady, setStreamReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDeviceId, setVideoDeviceId] = useState<string | null>(null);
  const [audioDeviceId, setAudioDeviceId] = useState<string | null>(null);
  // Video FX preferences (background blur + portrait lighting). Both run
  // through the same ZoomFX processor on the live preview track, so the
  // candidate/recruiter sees exactly what will be published.
  const [fx, setFx] = useState<FxMode>(FX_OFF);

  const tearDownStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const ctx = audioCtxRef.current;
    if (ctx) {
      ctx.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
    const lk = lkVideoTrackRef.current;
    if (lk) {
      // Detach + stop the wrapped LiveKit track, which also tears down any
      // attached processor (MediaPipe segmenter, canvas worker, etc.).
      try {
        if (videoRef.current) lk.detach(videoRef.current);
      } catch {
        // already detached
      }
      lk.stop();
      lkVideoTrackRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const wireAudioMeter = useCallback((stream: MediaStream) => {
    const AudioCtor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new AudioCtor();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    audioCtxRef.current = ctx;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      setAudioLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const acquireStream = useCallback(
    async (videoId: string | null, audioId: string | null) => {
      // Stop the old one BEFORE opening the new — otherwise some browsers
      // refuse to give two consumers on the same device.
      tearDownStream();
      setAudioLevel(0);
      // Secure Context check: getUserMedia is gated to https / localhost.
      // On HTTP+LAN-IP `navigator.mediaDevices` is literally undefined.
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError(
          "Браузер блокирует доступ к камере по обычному HTTP. " +
            "Откройте страницу по https:// или через localhost."
        );
        setStreamReady(false);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoId
            ? { deviceId: { exact: videoId } }
            : { facingMode: "user" },
          audio: audioId ? { deviceId: { exact: audioId } } : true,
        });
        streamRef.current = stream;

        // Wrap the raw video MediaStreamTrack in a LiveKit LocalVideoTrack so
        // we can run the same BackgroundBlur processor here in PreJoin that
        // we use in the call. Two wins:
        //   1. The preview honestly shows what the candidate/recruiter will
        //      look like (not a CSS-blurred full frame).
        //   2. MediaPipe wasm + the segmenter model are warmed up by the
        //      time we hit InCall, so the toggle reacts instantly.
        const videoMst = stream.getVideoTracks()[0];
        if (videoMst) {
          const lk = new LocalVideoTrack(videoMst);
          lkVideoTrackRef.current = lk;
          const mode = fxModeRef.current;
          if (fxActive(mode)) {
            try {
              const proc = new ZoomFXProcessor(mode);
              fxProcRef.current = proc;
              await lk.setProcessor(proc);
            } catch (e) {
              console.warn("PreJoin fx init failed", e);
            }
          }
          if (videoRef.current) lk.attach(videoRef.current);
        } else if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        wireAudioMeter(stream);
        setStreamReady(true);
        setError(null);

        // After permissions are granted, enumerateDevices returns labels.
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === "videoinput");
        const mics = all.filter((d) => d.kind === "audioinput");
        setVideoDevices(cams);
        setAudioDevices(mics);

        // Lock in the active deviceIds so the selects match the live tracks.
        const v = stream.getVideoTracks()[0]?.getSettings().deviceId ?? null;
        const a = stream.getAudioTracks()[0]?.getSettings().deviceId ?? null;
        setVideoDeviceId(v ?? videoId ?? cams[0]?.deviceId ?? null);
        setAudioDeviceId(a ?? audioId ?? mics[0]?.deviceId ?? null);
      } catch (e) {
        const msg =
          e instanceof Error
            ? e.message
            : "не удалось получить доступ к камере";
        setError(msg);
        setStreamReady(false);
      }
    },
    [tearDownStream, wireAudioMeter]
  );

  // Apply / update the ZoomFX processor on the preview track. Toggling
  // either effect on an already-attached processor is instant (no wasm /
  // worker re-init); we only stop it when both effects go off.
  const applyFx = useCallback(async (next: FxMode) => {
    fxModeRef.current = next;
    const lk = lkVideoTrackRef.current;
    if (!lk) return;
    try {
      if (!fxActive(next)) {
        fxProcRef.current = null;
        await lk.stopProcessor();
        return;
      }
      if (fxProcRef.current) {
        fxProcRef.current.setMode(next);
      } else {
        const proc = new ZoomFXProcessor(next);
        fxProcRef.current = proc;
        await lk.setProcessor(proc);
      }
    } catch (e) {
      console.warn("PreJoin fx toggle failed", e);
    }
  }, []);

  // Initial acquire on mount + cleanup on unmount.
  useEffect(() => {
    acquireStream(null, null);
    return () => {
      tearDownStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to hot-plug events (USB camera plugged in, etc.).
  useEffect(() => {
    if (!navigator.mediaDevices) return;
    const onChange = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        setVideoDevices(all.filter((d) => d.kind === "videoinput"));
        setAudioDevices(all.filter((d) => d.kind === "audioinput"));
      } catch {
        // ignore
      }
    };
    navigator.mediaDevices.addEventListener?.("devicechange", onChange);
    return () =>
      navigator.mediaDevices.removeEventListener?.("devicechange", onChange);
  }, []);

  const onPickVideo = (id: string) => {
    setVideoDeviceId(id);
    acquireStream(id, audioDeviceId);
  };

  const onPickAudio = (id: string) => {
    setAudioDeviceId(id);
    acquireStream(videoDeviceId, id);
  };

  const handleJoin = () => {
    tearDownStream();
    onJoin({ videoDeviceId, audioDeviceId, fx });
  };

  return (
    <div className="prejoin">
      <TopBar vacancy={meeting.vacancy_title} />

      <div className="prejoin-body">
        <div className="prejoin-preview">
          {error ? (
            <div className="prejoin-error">
              <Icon name="info" size={28} />
              <div>{error}</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                Разрешите доступ к камере и микрофону в настройках браузера и
                перезагрузите страницу.
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scaleX(-1)",
                borderRadius: 16,
                background: "oklch(0.13 0.008 280)",
              }}
            />
          )}
        </div>

        <aside className="prejoin-form">
          <div className="prejoin-eyebrow">
            {role === "candidate" ? "видео-интервью" : "вы — рекрутёр"}
          </div>
          <h1 className="prejoin-title">
            {role === "candidate" ? "Добро пожаловать!" : "Готовы к встрече?"}
          </h1>
          <p className="prejoin-lede">
            {role === "candidate"
              ? meeting.vacancy_title
                ? `Это видео-интервью с рекрутёром по вакансии «${meeting.vacancy_title}». Длительность — около 20 минут.`
                : "Это видео-интервью с рекрутёром HRDeva. Длительность — около 20 минут."
              : meeting.candidate_name
                ? `${meeting.candidate_name} подключится через несколько минут. Запись начнётся после явного согласия кандидата.`
                : "Кандидат подключится через несколько минут. Запись начнётся после явного согласия."}
          </p>

          <div className="prejoin-checks">
            <DeviceRow
              icon="cam"
              label="Камера"
              ok={streamReady && !error}
              devices={videoDevices}
              selectedId={videoDeviceId}
              fallback="системная камера"
              onChange={onPickVideo}
            />
            <DeviceRow
              icon="mic"
              label="Микрофон"
              ok={streamReady && !error}
              devices={audioDevices}
              selectedId={audioDeviceId}
              fallback="системный микрофон"
              waveLevel={streamReady ? audioLevel : 0}
              onChange={onPickAudio}
            />
          </div>

          <FxToggle
            icon="blur"
            label="Размыть фон"
            checked={fx.blur}
            onChange={(on) => {
              const next = { ...fx, blur: on };
              setFx(next);
              applyFx(next);
            }}
          />
          <FxToggle
            icon="sun"
            label="Портретный свет"
            checked={fx.portrait}
            onChange={(on) => {
              const next = { ...fx, portrait: on };
              setFx(next);
              applyFx(next);
            }}
            hint="можно сменить во встрече"
          />

          <button
            className="prejoin-cta"
            disabled={!streamReady || !!error}
            onClick={handleJoin}
          >
            {role === "candidate" ? "Войти в комнату" : "Открыть комнату"}
          </button>

          <p className="prejoin-fineprint">
            Нажимая «Войти», вы соглашаетесь с обработкой персональных данных в
            рамках интервью.
          </p>
        </aside>
      </div>
    </div>
  );
}

function TopBar({ vacancy }: { vacancy: string | null }) {
  return (
    <div className="vr-topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HRDMark size={22} />
        <span className="vr-topbar-mono">HRDeva · Видеокабинет</span>
      </div>
      <div className="vr-topbar-divider" />
      <div className="vr-topbar-meta">
        <div className="vr-topbar-title">
          {vacancy ? `Интервью · ${vacancy}` : "Видео-интервью"}
        </div>
        <div className="vr-topbar-sub">подготовка · WebRTC P2P</div>
      </div>
    </div>
  );
}

function DeviceRow({
  icon,
  label,
  ok,
  devices,
  selectedId,
  fallback,
  waveLevel,
  onChange,
}: {
  icon: string;
  label: string;
  ok: boolean;
  devices: MediaDeviceInfo[];
  selectedId: string | null;
  fallback: string;
  waveLevel?: number;
  onChange: (deviceId: string) => void;
}) {
  // Browsers return an empty deviceId entry until the user grants permission.
  // Once granted, devices have labels; before that we show a generic note.
  const hasReal = devices.some((d) => d.deviceId && d.label);
  return (
    <div className="prejoin-check">
      <div
        className="prejoin-check-icon"
        style={{
          background: ok ? "oklch(0.28 0.05 155)" : "var(--panel-2)",
          color: ok ? "var(--green)" : "var(--text-3)",
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</div>
        {hasReal ? (
          <select
            value={selectedId ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={{
              marginTop: 3,
              width: "100%",
              maxWidth: "100%",
              padding: "3px 6px 3px 0",
              background: "transparent",
              color: "var(--text)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              border: 0,
              borderBottom: "1px dashed var(--border)",
              cursor: "pointer",
              appearance: "none",
            }}
          >
            {devices.map((d) => (
              <option
                key={d.deviceId}
                value={d.deviceId}
                style={{ background: "var(--panel-2)", color: "var(--text)" }}
              >
                {d.label || fallback}
              </option>
            ))}
          </select>
        ) : (
          <div className="prejoin-check-sub">
            {ok ? fallback : "запрашиваем доступ…"}
          </div>
        )}
      </div>
      {typeof waveLevel === "number" && <Wave level={waveLevel} active={ok} />}
      {ok && <Icon name="check" size={14} stroke={2.5} />}
    </div>
  );
}

function FxToggle({
  icon,
  label,
  checked,
  onChange,
  hint,
}: {
  icon: string;
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  hint?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 8,
        borderRadius: 10,
        border: "1px solid var(--border-faint)",
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: "var(--green)" }}
      />
      <Icon name={icon} size={14} />
      <span style={{ flex: 1 }}>{label}</span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>{hint}</span>
      )}
    </label>
  );
}

function Wave({ level, active }: { level: number; active: boolean }) {
  if (!active) return null;
  const bars = 5;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, height: 16 }}>
      {Array.from({ length: bars }).map((_, i) => {
        const phase = (i + 1) / bars;
        const h = Math.max(0.18, Math.min(1, level * (0.4 + phase)));
        return (
          <span
            key={i}
            style={{
              width: 2.5,
              height: "100%",
              background: "var(--green)",
              borderRadius: 1,
              transform: `scaleY(${h})`,
              transformOrigin: "center",
              transition: "transform 80ms ease",
            }}
          />
        );
      })}
    </div>
  );
}
